import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import FigureComponent from './FigureComponent';

export interface FigureOptions {
    HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        figure: {
            /**
             * Add a figure with image and caption
             */
            setFigure: (options: { src: string | null; alt?: string; caption?: string }) => ReturnType;
        };
    }
}

export const Figure = Node.create<FigureOptions>({
    name: 'figure',

    addOptions() {
        return {
            HTMLAttributes: {},
        };
    },

    group: 'block',

    content: '',

    draggable: true,

    addAttributes() {
        return {
            src: {
                default: null,
                parseHTML: element => element.querySelector('img')?.getAttribute('src'),
                renderHTML: attributes => {
                    if (!attributes.src) {
                        return {};
                    }

                    return {
                        src: attributes.src,
                    };
                },
            },
            alt: {
                default: null,
                parseHTML: element => element.querySelector('img')?.getAttribute('alt'),
                renderHTML: attributes => {
                    if (!attributes.alt) {
                        return {};
                    }

                    return {
                        alt: attributes.alt,
                    };
                },
            },
            caption: {
                default: '',
                parseHTML: element => element.querySelector('figcaption')?.textContent || '',
                renderHTML: attributes => {
                    if (!attributes.caption) {
                        return {};
                    }

                    return {
                        caption: attributes.caption,
                    };
                },
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'figure',
                getAttrs: element => {
                    const img = element.querySelector('img');
                    const caption = element.querySelector('figcaption');

                    return {
                        src: img?.getAttribute('src') || null,
                        alt: img?.getAttribute('alt') || null,
                        caption: caption?.textContent || '',
                    };
                },
            },
        ];
    },

    renderHTML({ HTMLAttributes, node }) {
        return [
            'figure',
            mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
            [
                'img',
                {
                    src: node.attrs.src,
                    alt: node.attrs.alt,
                },
            ],
            [
                'figcaption',
                {},
                node.attrs.caption || '',
            ],
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(FigureComponent);
    },

    addCommands() {
        return {
            setFigure:
                options =>
                    ({ commands }) => {
                        return commands.insertContent({
                            type: this.name,
                            attrs: options,
                        });
                    },
        };
    },
});

export default Figure; 